import { useEffect, useState } from "react";
import { BetaBadge } from "@/shared/components/beta-badge";
import { ComingSoonBadge } from "./coming-soon-badge";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Loader2Icon,
  PlusIcon,
  SparklesIcon,
  Trash2Icon,
} from "lucide-react";
import {
  MATRIX_PROMPT_PRESETS,
  matrixPresetPrompt,
  matrixPromptPresetIdForPrompt,
} from "../lib/prompt-presets";
import { PromptTextareaWithColumnMentions } from "./prompt-textarea-with-column-mentions";
import { COLUMN_TYPE_ICONS, COLUMN_TYPE_LABELS } from "../lib/column-type-meta";
import { isColumnPrompted } from "@/domains/matrix";
import type { MatrixColumnDef, MatrixColumnType } from "@/domains/matrix";

type ColumnHeaderPopoverSubmitValues = {
  label: string;
  type: MatrixColumnType;
  displayDescription: string;
};

type ColumnHeaderPopoverProps = {
  mode: "create" | "edit";
  column?: MatrixColumnDef;
  allColumns: MatrixColumnDef[];
  onSubmit: (values: ColumnHeaderPopoverSubmitValues) => void;
  onRemove?: () => void;
  isSubmitting?: boolean;
  submitError?: string | null;
  // The dropdown renders in a portal, so the popover must not read it as an outside click.
  onNestedSelectOpenChange?: (open: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  disabled?: boolean;
};

export function ColumnHeaderPopover({
  mode,
  column,
  allColumns,
  onSubmit,
  onRemove,
  isSubmitting = false,
  submitError = null,
  onNestedSelectOpenChange,
  onDirtyChange,
  disabled = false,
}: ColumnHeaderPopoverProps) {
  const subjectColumn = allColumns.find(
    (candidate) =>
      !isColumnPrompted(candidate) && candidate.key !== column?.key,
  );
  const subjectMention = subjectColumn ? `@${subjectColumn.key}` : null;

  const [label, setLabel] = useState(column?.label ?? "");
  // New columns are text-only; preserve the type of existing columns.
  const type = column?.type ?? "text";
  const ColumnTypeIcon = COLUMN_TYPE_ICONS[type];
  const [displayDescription, setDisplayDescription] = useState(
    column?.displayDescription ?? "",
  );
  const [displayDescriptionPresetId, setDisplayDescriptionPresetId] = useState(
    () =>
      matrixPromptPresetIdForPrompt(column?.displayDescription, subjectMention),
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isEnriched, setIsEnriched] = useState(() =>
    column ? isColumnPrompted(column) : false,
  );

  const initialLabel = column?.label ?? "";
  const initialDisplayDescription = column?.displayDescription ?? "";
  const initialIsEnriched = column ? isColumnPrompted(column) : false;

  const isPromptLocked = mode === "edit" && initialIsEnriched;

  const isDirty =
    label !== initialLabel ||
    isEnriched !== initialIsEnriched ||
    (isEnriched && displayDescription !== initialDisplayDescription);

  const canSubmit =
    !disabled &&
    label.trim().length > 0 &&
    (!isEnriched || displayDescription.trim().length > 0) &&
    (mode === "create" || isDirty);

  const submitHint = isSubmitting
    ? null
    : !label.trim()
      ? "Give the column a name."
      : isEnriched && !displayDescription.trim()
        ? "An analysis step needs a prompt."
        : null;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  function handleDiscard() {
    setLabel(initialLabel);
    setDisplayDescription(initialDisplayDescription);
    setDisplayDescriptionPresetId(
      matrixPromptPresetIdForPrompt(initialDisplayDescription, subjectMention),
    );
    setIsEnriched(initialIsEnriched);
  }

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit({
      label: label.trim(),
      type,
      // An empty description turns an enriched column back into an input one.
      displayDescription: isPromptLocked
        ? initialDisplayDescription
        : isEnriched
          ? displayDescription.trim()
          : "",
    });
  }

  const submitLabel = mode === "create" ? "Add Column" : "Save Column";
  const SubmitIcon = mode === "create" ? PlusIcon : CheckIcon;

  return (
    <fieldset
      disabled={disabled}
      className="flex max-h-[70vh] w-full flex-col overflow-hidden disabled:cursor-not-allowed"
    >
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        <div className="border-input focus-within:border-ring focus-within:ring-ring/50 dark:bg-input/30 flex h-9 items-stretch overflow-hidden rounded-md border shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]">
          <Input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Column name"
            aria-label="Column name"
            className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm font-medium shadow-none focus-visible:border-0 focus-visible:ring-0 dark:bg-transparent"
          />
          <span
            className="border-input text-muted-foreground flex shrink-0 items-center gap-1.5 border-l px-2.5 text-xs"
            aria-label={`Column type: ${COLUMN_TYPE_LABELS[type]}`}
          >
            <ColumnTypeIcon className="size-3.5" aria-hidden />
            {COLUMN_TYPE_LABELS[type]}
          </span>
        </div>

        <fieldset
          disabled={isPromptLocked}
          className="border-primary/20 bg-primary/5 space-y-3 rounded-md border p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <Label
              htmlFor="matrix-enrich-column"
              className="flex cursor-pointer items-center gap-1.5 text-xs"
            >
              <SparklesIcon className="text-primary size-3.5" aria-hidden />
              Add analysis step
            </Label>
            <Checkbox
              id="matrix-enrich-column"
              checked={isEnriched}
              onCheckedChange={(checked) => setIsEnriched(checked === true)}
            />
          </div>

          {!isEnriched ? null : (
            <>
              <div className="space-y-1.5">
                <Label
                  htmlFor="matrix-prompt-preset"
                  className="text-muted-foreground text-xs"
                >
                  Preset templates
                </Label>
                <Select
                  disabled={isPromptLocked}
                  value={displayDescriptionPresetId}
                  onValueChange={(id) => {
                    setDisplayDescriptionPresetId(id);
                    if (id === "custom") return;
                    const preset = MATRIX_PROMPT_PRESETS.find(
                      (p) => p.id === id,
                    );
                    if (preset)
                      setDisplayDescription(
                        matrixPresetPrompt(preset, subjectMention),
                      );
                  }}
                  onOpenChange={onNestedSelectOpenChange}
                >
                  <SelectTrigger
                    id="matrix-prompt-preset"
                    className="bg-background dark:bg-background w-full"
                    size="sm"
                  >
                    <SelectValue placeholder="Custom prompt or preset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom prompt</SelectItem>
                    {MATRIX_PROMPT_PRESETS.map((preset) => (
                      <SelectItem
                        key={preset.id}
                        value={preset.id}
                        className="pr-2 data-[state=checked]:pr-8 [&>span:last-child]:flex-1"
                      >
                        {preset.label}
                        <BetaBadge size="sm" className="ml-auto" />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="matrix-column-prompt"
                  className="text-muted-foreground text-xs"
                >
                  Prompt
                </Label>
                <PromptTextareaWithColumnMentions
                  id="matrix-column-prompt"
                  value={displayDescription}
                  onChange={(next) => {
                    setDisplayDescription(next);
                    setDisplayDescriptionPresetId(
                      matrixPromptPresetIdForPrompt(next, subjectMention),
                    );
                  }}
                  columns={allColumns}
                  excludeColumnKey={column?.key ?? null}
                  className="bg-background dark:bg-background"
                  rows={5}
                  placeholder="Enter the prompt for this column..."
                  onMenuOpenChange={onNestedSelectOpenChange}
                />
              </div>

              <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                <CollapsibleTrigger className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex flex-wrap items-center gap-1.5 rounded-md py-1 text-left text-xs font-medium transition-colors focus-visible:ring-[3px] focus-visible:outline-none">
                  {showAdvanced ? (
                    <ChevronUpIcon className="size-3.5 shrink-0" />
                  ) : (
                    <ChevronDownIcon className="size-3.5 shrink-0" />
                  )}
                  <span className="min-w-0">Advanced settings</span>
                  <ComingSoonBadge />
                </CollapsibleTrigger>
                <CollapsibleContent className="text-muted-foreground bg-muted/50 border-border/60 mt-2 space-y-2 rounded-md border p-2 text-xs">
                  {[
                    ["Add format instructions", "-"],
                    ["Context scope", "All"],
                    ["Restrict sources", "Off"],
                    ["Model", "Auto"],
                  ].map(([setting, value]) => (
                    <div
                      key={setting}
                      className="flex items-center justify-between"
                    >
                      <span>{setting}</span>
                      <span>{value}</span>
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </fieldset>
        {isPromptLocked ? (
          <p className="text-muted-foreground text-xs">
            Editing existing research prompts is temporarily unavailable.
          </p>
        ) : isEnriched ? (
          <p className="text-muted-foreground text-xs">
            Saving does not start research. Use Start Analysis to research this
            column.
          </p>
        ) : null}

        {submitError ? (
          <Alert variant="destructive">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      <div className="bg-background shrink-0 border-t p-3">
        <Button
          className="w-full gap-1.5"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? (
            <Loader2Icon className="size-3.5 animate-spin" />
          ) : (
            <SubmitIcon className="size-3.5" />
          )}
          {submitLabel}
        </Button>

        {submitHint ? (
          <p className="text-muted-foreground mt-2 text-center text-xs">
            {submitHint}
          </p>
        ) : null}

        {isDirty ? (
          <div className="mt-3 flex items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground min-w-0 truncate">
              Unsaved changes
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={handleDiscard}
              disabled={isSubmitting}
            >
              Discard
            </Button>
          </div>
        ) : mode === "edit" && onRemove ? (
          <div className="mt-3 flex items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground min-w-0 truncate">
              No longer needed?
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive shrink-0 gap-1.5"
              onClick={onRemove}
            >
              <Trash2Icon className="size-3.5" />
              Remove column
            </Button>
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
